import React, { useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { Button } from 'ui-components/components/button';

import OpenRateCalcHotkey from '../../../components/hotkeys/advanced-hotkeys/open-rate-calc';
import { PackageService } from '../../../services/package-service';
import Global from '../../../store/global-selectors';
import { openRateBrowserModal } from '../../../store/rate-browser/rate-browser.actions';
import { useTrackRateBrowser } from '../../rate-viewer/use-track-rate-browser';

import { MessageDescriptorPropType } from '../../../prop-types/intl';

import { appHeaderMessages } from '../messages';
import style from '../app-header.module.less';
import { WorkflowSteps } from '../../../tracking/use-workflow-tracking';

export const getRateBrowserDefaults = createSelector(
  Global.selectSellerDefaultShipFromId,
  Global.selectSellerSupportCountry,
  (shipFromId, shipToCountryCode) => {
    const allPackageTypesId = PackageService.getAllPackageTypesId();
    return {
      shipFromId,
      shipToCountryCode,
      selectedPackage: {
        packageTypeId: allPackageTypesId,
        id: allPackageTypesId,
        nameMessageId: 'allPackageTypes', // Reference to message key for translation
        dimensions: {
          length: 0,
          width: 0,
          height: 0,
          unit: 'Unknown',
        },
      },
    };
  },
);

const getRateBrowserButtonOnClick = createSelector(
  (openRateBrowser) => openRateBrowser,
  (_, rateBrowserDefaults) => rateBrowserDefaults,
  (openRateBrowser, rateBrowserDefaults) => () => openRateBrowser(rateBrowserDefaults),
);

export const RateBrowserButton = ({ displayText = null, origin }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const rateBrowserDefaults = useSelector((state) => getRateBrowserDefaults(state));
  const trackRateBrowser = useTrackRateBrowser();

  // Update the package name with the internationalized message
  if (rateBrowserDefaults.selectedPackage && rateBrowserDefaults.selectedPackage.nameMessageId) {
    rateBrowserDefaults.selectedPackage.name = intl.formatMessage(appHeaderMessages[rateBrowserDefaults.selectedPackage.nameMessageId]);
  }

  const openRateBrowser = useCallback(
    (defaults) => {
      dispatch(openRateBrowserModal(defaults));
      trackRateBrowser(WorkflowSteps.START, { source: origin });
    },
    [dispatch, origin, trackRateBrowser],
  );

  return (
    <OpenRateCalcHotkey rateBrowserDefaults={rateBrowserDefaults}>
      {displayText && (
        <FormattedMessage
          {...displayText}
          values={{
            button: (chunks) => (
              <Button onClick={getRateBrowserButtonOnClick(openRateBrowser, rateBrowserDefaults)} tabIndex={-1} theme='link'>
                {chunks}
              </Button>
            ),
          }}
        />
      )}
      {!displayText && (
        <Button
          className={style['rate-browser-button']}
          onClick={getRateBrowserButtonOnClick(openRateBrowser, rateBrowserDefaults)}
          tabIndex={-1}
          theme='unstyled'
        >
          <FormattedMessage {...appHeaderMessages.rates} />
        </Button>
      )}
    </OpenRateCalcHotkey>
  );
};

RateBrowserButton.propTypes = {
  displayText: MessageDescriptorPropType,
  origin: PropTypes.string.isRequired,
};
